import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  ButtonGroup,
  Button,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import { Close as CloseIcon, Search as SearchIcon } from '@material-ui/icons';
import type { AssetUtilization } from '../../../_models/schedule.model';

interface AssetUtilizationModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  assetType: 'room' | 'instructor';
  data: AssetUtilization[];
}

type ViewType = 'all' | 'top5' | 'top10' | 'bottom5' | 'bottom10';

const AssetUtilizationModal: React.FC<AssetUtilizationModalProps> = ({
  open,
  onClose,
  title,
  assetType,
  data,
}) => {
  const [viewType, setViewType] = useState<ViewType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'utilization'>('utilization');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const hoursField = assetType === 'room' ? 'scheduledHours' : 'teachingHours';

  const filteredAndSortedData = useMemo(() => {
    // Safety check: ensure data is an array
    if (!Array.isArray(data)) {
      return [];
    }

    let result = [...data];

    // Apply search filter
    if (searchTerm) {
      result = result.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort data
    result.sort((a, b) => {
      if (sortBy === 'name') {
        return sortOrder === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else {
        return sortOrder === 'asc'
          ? a.utilizationRate - b.utilizationRate
          : b.utilizationRate - a.utilizationRate;
      }
    });

    // Apply view type filter
    switch (viewType) {
      case 'top5':
        result = result.slice(0, 5);
        break;
      case 'top10':
        result = result.slice(0, 10);
        break;
      case 'bottom5':
        result = result.slice(-5).reverse();
        break;
      case 'bottom10':
        result = result.slice(-10).reverse();
        break;
      default:
        // 'all' - no slicing
        break;
    }

    return result;
  }, [data, searchTerm, sortBy, sortOrder, viewType]);

  const handleSort = (field: 'name' | 'utilization') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'utilization' ? 'desc' : 'asc');
    }
  };

  const getUtilizationColor = (rate: number): string => {
    if (rate < 30) return '#f56565'; // Red - under-utilized
    if (rate < 60) return '#ed8936'; // Orange - moderate
    if (rate < 85) return '#48bb78'; // Green - good
    return '#667eea'; // Blue - high utilization
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Controls */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <ButtonGroup size="small" color="primary">
              <Button
                variant={viewType === 'all' ? 'contained' : 'outlined'}
                onClick={() => setViewType('all')}
              >
                All ({data?.length || 0})
              </Button>
              <Button
                variant={viewType === 'top5' ? 'contained' : 'outlined'}
                onClick={() => setViewType('top5')}
              >
                Top 5
              </Button>
              <Button
                variant={viewType === 'top10' ? 'contained' : 'outlined'}
                onClick={() => setViewType('top10')}
              >
                Top 10
              </Button>
              <Button
                variant={viewType === 'bottom5' ? 'contained' : 'outlined'}
                onClick={() => setViewType('bottom5')}
              >
                Bottom 5
              </Button>
              <Button
                variant={viewType === 'bottom10' ? 'contained' : 'outlined'}
                onClick={() => setViewType('bottom10')}
              >
                Bottom 10
              </Button>
            </ButtonGroup>

            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              style={{ width: 250 }}
            />
          </Box>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow style={{ background: 'var(--surface-page)' }}>
                <TableCell>
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => handleSort('name')}
                  >
                    {assetType === 'room' ? 'Room' : 'Instructor'}
                    {sortBy === 'name' && (
                      <span style={{ marginLeft: 4 }}>
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">Available Hours</TableCell>
                <TableCell align="right">
                  {assetType === 'room' ? 'Scheduled Hours' : 'Teaching Hours'}
                </TableCell>
                <TableCell align="right">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    style={{ cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => handleSort('utilization')}
                  >
                    Utilization
                    {sortBy === 'utilization' && (
                      <span style={{ marginLeft: 4 }}>
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ padding: '2rem' }}>
                    <Typography variant="body2" color="textSecondary">
                      No {assetType}s found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedData.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.name}</TableCell>
                    <TableCell align="right">{item.availableHours.toFixed(1)}h</TableCell>
                    <TableCell align="right">
                      {((item as any)[hoursField] || 0).toFixed(1)}h
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end">
                        <div
                          style={{
                            width: 80,
                            height: 20,
                            background: 'var(--surface-sunken)',
                            borderRadius: 10,
                            overflow: 'hidden',
                            position: 'relative',
                            marginRight: 8,
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(item.utilizationRate, 100)}%`,
                              height: '100%',
                              background: getUtilizationColor(item.utilizationRate),
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: 600,
                            color: getUtilizationColor(item.utilizationRate),
                            minWidth: 50,
                            textAlign: 'right',
                          }}
                        >
                          {item.utilizationRate}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary */}
        <Box mt={2} p={2} bgcolor="#f7fafc" borderRadius={2}>
          <Typography variant="body2" color="textSecondary">
            Showing {filteredAndSortedData.length} of {data?.length || 0} {assetType}s
            {searchTerm && ` matching "${searchTerm}"`}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AssetUtilizationModal;
